import type { ParsedSheetCommand } from "./commandParser";
import type { SheetAccountEntry, SheetLineEntry } from "../../types/sheetDocument";
import type { TAccountCatalogItem } from "./types";
import { tokenizeCommandInput } from "./commandTokenization";
import { buildCommentReferenceSuggestions } from "./commentUtils";

export interface SheetCommandDraft {
  tAccounts: SheetAccountEntry[];
  uberCommentsByLetter: Record<string, string>;
  forceTwoDecimalDisplay: boolean;
  documentTitle: string;
  nextAutoLetterIndex: number;
  nextEntrySequence: number;
  catalog: TAccountCatalogItem[];
  showHelp: boolean;
}

export class SheetCommandExecutionError extends Error {
  limit?: { kind: "taccounts" | "transactions"; value: number };

  constructor(message: string, limit?: { kind: "taccounts" | "transactions"; value: number }) {
    super(message);
    this.name = "SheetCommandExecutionError";
    this.limit = limit;
  }
}

interface ApplySheetCommandBatchArgs {
  commands: ParsedSheetCommand[];
  initialDraft: Omit<SheetCommandDraft, "showHelp">;
  maxTAccountsPerDocument: number | null;
  maxTransactionsPerDocument: number | null;
}

const cloneAccounts = (accounts: SheetAccountEntry[]): SheetAccountEntry[] => accounts.map((account) => ({
  ...account,
  entries: account.entries.map((entry) => ({ ...entry })),
}));

const resolveCatalogItem = (catalog: TAccountCatalogItem[], accountToken: string): TAccountCatalogItem | null => {
  const normalizedToken = accountToken.trim().toLowerCase();
  return catalog.find((item) => (
    item.name.trim().toLowerCase() === normalizedToken
    || item.alias?.trim().toLowerCase() === normalizedToken
  )) ?? null;
};

const resolveSequence = (accounts: SheetAccountEntry[], reference: string): number | null => {
  const entries = accounts.flatMap((account) => account.entries)
    .filter((entry) => !entry.isBeginningBalance)
    .slice()
    .sort((left, right) => left.sequence - right.sequence);
  const totals = new Map<string, number>();
  entries.forEach((entry) => totals.set(entry.letter, (totals.get(entry.letter) ?? 0) + 1));
  const progress = new Map<string, number>();

  for (const entry of entries) {
    const index = (progress.get(entry.letter) ?? 0) + 1;
    progress.set(entry.letter, index);
    const displayReference = totals.get(entry.letter) === 1 ? entry.letter : `${entry.letter}.${index}`;
    if (displayReference.toLowerCase() === reference.toLowerCase()) {
      return entry.sequence;
    }
  }
  return null;
};

export function applySheetCommandBatch({
  commands,
  initialDraft,
  maxTAccountsPerDocument,
  maxTransactionsPerDocument,
}: ApplySheetCommandBatchArgs): SheetCommandDraft {
  const draft: SheetCommandDraft = {
    ...initialDraft,
    tAccounts: cloneAccounts(initialDraft.tAccounts),
    uberCommentsByLetter: { ...initialDraft.uberCommentsByLetter },
    catalog: initialDraft.catalog.map((item) => ({ ...item })),
    showHelp: false,
  };

  const assertLimits = (accounts: SheetAccountEntry[]) => {
    if (maxTAccountsPerDocument !== null && accounts.length > maxTAccountsPerDocument) {
      throw new SheetCommandExecutionError("T-account visualization limit reached.", {
        kind: "taccounts",
        value: maxTAccountsPerDocument,
      });
    }
    const transactionCount = accounts.reduce((count, account) => count + account.entries.length, 0);
    if (maxTransactionsPerDocument !== null && transactionCount > maxTransactionsPerDocument) {
      throw new SheetCommandExecutionError("Transaction limit reached.", {
        kind: "transactions",
        value: maxTransactionsPerDocument,
      });
    }
  };

  for (const parsedCommand of commands) {
    const commandTokens = tokenizeCommandInput(parsedCommand.raw.trim()).tokens;
    const amountTokenIndex = (parsedCommand.command === "credit" || parsedCommand.command === "debit")
      && parsedCommand.accountNumber
      && commandTokens[1]?.quoteChar
      ? 3
      : 2;
    if ("amount" in parsedCommand && commandTokens[amountTokenIndex]?.value.includes(".")) {
      draft.forceTwoDecimalDisplay = true;
    }

    if (parsedCommand.command === "clear") {
      draft.tAccounts = [];
      draft.uberCommentsByLetter = {};
      draft.forceTwoDecimalDisplay = false;
      draft.nextAutoLetterIndex = 0;
      draft.nextEntrySequence = 1;
      continue;
    }
    if (parsedCommand.command === "comment") {
      draft.uberCommentsByLetter[parsedCommand.letter] = parsedCommand.comment;
      continue;
    }
    if (parsedCommand.command === "title") {
      draft.documentTitle = parsedCommand.title;
      continue;
    }
    if (parsedCommand.command === "help") {
      draft.showHelp = true;
      continue;
    }
    if (parsedCommand.command === "delete") {
      const targetLetter = parsedCommand.reference.toLowerCase();
      if (!draft.tAccounts.some((account) => account.entries.some((entry) => entry.letter.toLowerCase() === targetLetter))) {
        throw new SheetCommandExecutionError(`No line items found for reference '${parsedCommand.reference}'.`);
      }
      draft.tAccounts = draft.tAccounts.map((account) => ({
        ...account,
        entries: account.entries.filter((entry) => entry.letter.toLowerCase() !== targetLetter),
      })).filter((account) => account.entries.length > 0);
      Object.keys(draft.uberCommentsByLetter).forEach((key) => {
        const normalizedKey = key.toLowerCase();
        if (normalizedKey === targetLetter || normalizedKey.startsWith(`${targetLetter}.`)) {
          delete draft.uberCommentsByLetter[key];
        }
      });
      continue;
    }
    if (parsedCommand.command === "edit") {
      const sequence = resolveSequence(draft.tAccounts, parsedCommand.reference);
      if (sequence === null) {
        throw new SheetCommandExecutionError(`No line item found for reference '${parsedCommand.reference}'.`);
      }
      draft.tAccounts = draft.tAccounts.map((account) => ({
        ...account,
        entries: account.entries.map((entry) => {
          if (entry.sequence !== sequence) return entry;
          const side = parsedCommand.side ?? (entry.debitAmount !== undefined ? "debit" : "credit");
          const amount = Math.abs(parsedCommand.amount);
          return {
            ...entry,
            debitAmount: side === "debit" ? amount : undefined,
            creditAmount: side === "credit" ? amount : undefined,
            comment: parsedCommand.comment ?? entry.comment,
          };
        }),
      }));
      continue;
    }
    if (parsedCommand.command === "move") {
      const sequence = resolveSequence(draft.tAccounts, parsedCommand.reference);
      if (sequence === null) {
        throw new SheetCommandExecutionError(`No line item found for reference '${parsedCommand.reference}'.`);
      }
      const target = resolveCatalogItem(draft.catalog, parsedCommand.account);
      if (!target) {
        throw new SheetCommandExecutionError(`No taccount found for '${parsedCommand.account}'. Add it from the Accounts panel first.`);
      }
      let movingEntry: SheetLineEntry | undefined;
      draft.tAccounts = draft.tAccounts.map((account) => ({
        ...account,
        entries: account.entries.filter((entry) => {
          if (entry.sequence === sequence) movingEntry = { ...entry };
          return entry.sequence !== sequence;
        }),
      })).filter((account) => account.entries.length > 0);
      if (!movingEntry) {
        throw new SheetCommandExecutionError(`No line item found for reference '${parsedCommand.reference}'.`);
      }
      const side = parsedCommand.side ?? (movingEntry.debitAmount !== undefined ? "debit" : "credit");
      const amount = Math.abs(movingEntry.debitAmount ?? movingEntry.creditAmount ?? 0);
      movingEntry.debitAmount = side === "debit" ? amount : undefined;
      movingEntry.creditAmount = side === "credit" ? amount : undefined;
      const targetAccount = draft.tAccounts.find((account) => account.taccountId === target.taccountId);
      if (targetAccount) {
        targetAccount.account = target.name;
        targetAccount.alias = target.alias ?? undefined;
        targetAccount.entries.push(movingEntry);
      } else {
        draft.tAccounts.push({
          taccountId: target.taccountId,
          account: target.name,
          alias: target.alias ?? undefined,
          entries: [movingEntry],
        });
      }
      assertLimits(draft.tAccounts);
      continue;
    }
    if (parsedCommand.command === "rename") {
      const source = resolveCatalogItem(draft.catalog, parsedCommand.oldAccount);
      if (!source) {
        throw new SheetCommandExecutionError(`No taccount found for '${parsedCommand.oldAccount}'. Add it from the Accounts panel first.`);
      }
      source.name = parsedCommand.newAccount.trim();
      draft.tAccounts.forEach((account) => {
        if (account.taccountId === source.taccountId) account.account = source.name;
      });
      continue;
    }

    const catalogItem = resolveCatalogItem(draft.catalog, parsedCommand.account);
    if (!catalogItem) {
      throw new SheetCommandExecutionError(`No taccount found for '${parsedCommand.account}'. Add it from the Accounts panel first.`);
    }
    if (parsedCommand.command === "bbal" && parsedCommand.action === "delete") {
      draft.tAccounts = draft.tAccounts.map((account) => account.taccountId === catalogItem.taccountId
        ? { ...account, entries: account.entries.filter((entry) => !entry.isBeginningBalance) }
        : account).filter((account) => account.entries.length > 0);
      continue;
    }

    const sequence = draft.nextEntrySequence;
    draft.nextEntrySequence += 1;
    let nextEntry: SheetLineEntry;
    if (parsedCommand.command === "bbal") {
      const amount = parsedCommand.amount ?? 0;
      const side = parsedCommand.side ?? (amount < 0 ? "credit" : "debit");
      nextEntry = {
        letter: "bbal",
        sequence,
        isBeginningBalance: true,
        comment: parsedCommand.comment,
        debitAmount: side === "debit" ? Math.abs(amount) : undefined,
        creditAmount: side === "credit" ? Math.abs(amount) : undefined,
      };
    } else {
      const alphabet = "abcdefghijklmnopqrstuvwxyz";
      let letter = parsedCommand.letter;
      if (!letter) {
        const usedReferences = new Set(buildCommentReferenceSuggestions([
          ...draft.tAccounts.flatMap((account) => account.entries.map((entry) => entry.letter)),
          ...Object.keys(draft.uberCommentsByLetter),
        ]));
        for (let attempts = 0; attempts < alphabet.length; attempts += 1) {
          const candidate = alphabet[draft.nextAutoLetterIndex % alphabet.length];
          draft.nextAutoLetterIndex += 1;
          if (!usedReferences.has(candidate)) {
            letter = candidate;
            break;
          }
        }
        if (!letter) {
          throw new SheetCommandExecutionError("No unused journal references remain. Specify a reference explicitly to add to an existing journal entry.");
        }
      }
      nextEntry = {
        letter,
        sequence,
        comment: parsedCommand.comment,
        debitAmount: parsedCommand.command === "debit" ? parsedCommand.amount : undefined,
        creditAmount: parsedCommand.command === "credit" ? parsedCommand.amount : undefined,
      };
    }
    const existingAccount = draft.tAccounts.find((account) => account.taccountId === catalogItem.taccountId);
    if (existingAccount) {
      existingAccount.account = catalogItem.name;
      existingAccount.alias = catalogItem.alias ?? undefined;
      existingAccount.entries = parsedCommand.command === "bbal"
        ? [nextEntry, ...existingAccount.entries.filter((entry) => !entry.isBeginningBalance)]
        : [...existingAccount.entries, nextEntry];
    } else {
      draft.tAccounts.push({
        taccountId: catalogItem.taccountId,
        account: catalogItem.name,
        alias: catalogItem.alias ?? undefined,
        entries: [nextEntry],
      });
    }
    assertLimits(draft.tAccounts);
  }

  return draft;
}